import React, { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Volume2, VolumeX } from "lucide-react";

/**
 * MeditationTimer — a self-contained, gentle 10-minute Samatha sit.
 *
 * No external audio. Bell sounds are synthesised on-the-fly via the
 * Web Audio API (sine wave + exponential decay) so the file size and
 * dependencies stay zero.
 *
 * Visual: a slow expanding-and-contracting circle on a 8-second cycle
 * (4s in, 4s out). It is a *companion* to the natural breath, never a
 * prescription. Inspired by the in-out rhythm of samatha-vipassanā.
 */

const DURATIONS = [
  { label: "5 min", minutes: 5 },
  { label: "10 min", minutes: 10 },
  { label: "15 min", minutes: 15 },
  { label: "20 min", minutes: 20 },
];

const BREATH_CYCLE = 8; // seconds — 4 in, 4 out

// --- Bell synthesis (Web Audio API) ---------------------------------------
function playBell(ctxRef, muted) {
  if (muted) return;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  if (!ctxRef.current) ctxRef.current = new Ctx();
  const ctx = ctxRef.current;
  if (ctx.state === "suspended") ctx.resume();

  const now = ctx.currentTime;
  // A soft singing-bowl-like tone: two harmonics with slow decay.
  const tones = [
    { freq: 528, gain: 0.32, decay: 4.0 },
    { freq: 1056, gain: 0.07, decay: 3.0 },
  ];
  tones.forEach(({ freq, gain, decay }) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, now);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(gain, now + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, now + decay);
    osc.connect(g).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + decay + 0.05);
  });
}

// --- Time formatting ------------------------------------------------------
function formatTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function MeditationTimer() {
  const [minutes, setMinutes] = useState(10);
  const [remaining, setRemaining] = useState(10 * 60);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [muted, setMuted] = useState(false);
  const [phase, setPhase] = useState("Breathe in"); // current breath phase
  const [breathProgress, setBreathProgress] = useState(0); // 0..1 across one cycle

  const audioCtxRef = useRef(null);
  const tickRef = useRef(null);
  const breathRef = useRef(null);
  const startedAtRef = useRef(null);

  // Reset remaining when duration changes (and we're idle)
  useEffect(() => {
    if (!running && !finished) setRemaining(minutes * 60);
  }, [minutes, running, finished]);

  // Countdown ticker
  useEffect(() => {
    if (!running) {
      clearInterval(tickRef.current);
      return;
    }
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(tickRef.current);
          setRunning(false);
          setFinished(true);
          // closing bells (three tolls)
          playBell(audioCtxRef, muted);
          setTimeout(() => playBell(audioCtxRef, muted), 1200);
          setTimeout(() => playBell(audioCtxRef, muted), 2400);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [running, muted]);

  // Breath cycle animator (using rAF for smoothness)
  useEffect(() => {
    if (!running) {
      cancelAnimationFrame(breathRef.current);
      return;
    }
    const animate = (ts) => {
      if (!startedAtRef.current) startedAtRef.current = ts;
      const elapsed = (ts - startedAtRef.current) / 1000; // seconds
      const t = (elapsed % BREATH_CYCLE) / BREATH_CYCLE; // 0..1
      setBreathProgress(t);
      setPhase(t < 0.5 ? "Breathe in" : "Breathe out");
      breathRef.current = requestAnimationFrame(animate);
    };
    breathRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(breathRef.current);
  }, [running]);

  const handleStart = () => {
    if (finished) {
      setRemaining(minutes * 60);
      setFinished(false);
    }
    if (!running) {
      // opening bell
      playBell(audioCtxRef, muted);
      startedAtRef.current = null;
    }
    setRunning((v) => !v);
  };

  const handleReset = () => {
    setRunning(false);
    setFinished(false);
    setRemaining(minutes * 60);
    setBreathProgress(0);
    setPhase("Breathe in");
  };

  // Compute scale: smooth ease in/out so circle "breathes"
  // First half (0..0.5) expands from 1 to 1.18, second half contracts.
  const scale = useMemo(() => {
    const t = breathProgress;
    const expand = (x) => 0.5 - 0.5 * Math.cos(Math.PI * x); // ease-in-out
    if (!running) return 1;
    const u = t < 0.5 ? expand(t * 2) : expand((1 - t) * 2);
    return 1 + 0.18 * u;
  }, [breathProgress, running]);

  const elapsed = minutes * 60 - remaining;
  const progressPct = (elapsed / (minutes * 60)) * 100;

  return (
    <section
      data-testid="meditation-timer"
      className="py-24 bg-pearl border-t border-navy/10"
    >
      <div className="section-container">
        <div className="max-w-2xl mx-auto text-center">
          <p className="eyebrow text-saffron">Sit with us</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">
            A short Samatha sit.
          </h2>
          <p className="mt-5 text-slate-700 leading-relaxed">
            Choose a duration. Tap <em>begin</em>. A soft bell will mark the start
            and the close. Let the breath be natural, the circle is only a
            companion, not an instruction.
          </p>
        </div>

        {/* Timer card */}
        <div className="mt-12 max-w-xl mx-auto bg-white border border-navy/10 rounded-2xl p-8 sm:p-10 relative overflow-hidden">
          {/* progress bar across the top */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-navy/5">
            <div
              className="h-full bg-gradient-to-r from-gold via-saffron to-gold transition-all duration-1000"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {/* Duration selector */}
          <div
            className="flex flex-wrap justify-center gap-2"
            data-testid="meditation-durations"
          >
            {DURATIONS.map((d) => (
              <button
                key={d.minutes}
                onClick={() => !running && setMinutes(d.minutes)}
                disabled={running}
                data-testid={`meditation-duration-${d.minutes}`}
                className={`px-4 py-1.5 text-xs uppercase tracking-[0.18em] rounded-full transition-all ${
                  minutes === d.minutes
                    ? "bg-navy text-pearl border border-navy"
                    : "bg-pearl text-navy/70 border border-navy/15 hover:border-navy/40"
                } ${running ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {d.label}
              </button>
            ))}
          </div>

          {/* Breathing circle */}
          <div className="mt-10 flex justify-center">
            <div
              className="relative flex items-center justify-center"
              style={{ width: 280, height: 280 }}
            >
              {/* outer halo */}
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(207,160,82,0.18) 0%, rgba(207,160,82,0.04) 60%, transparent 80%)",
                  transform: `scale(${scale * 1.05})`,
                  transition: "transform 60ms linear",
                }}
              />
              {/* main circle */}
              <div
                className="absolute rounded-full bg-gradient-to-br from-navy to-[#0A2540] shadow-2xl"
                style={{
                  width: 220,
                  height: 220,
                  transform: `scale(${scale})`,
                  transition: "transform 60ms linear",
                }}
              />
              {/* inner gold ring */}
              <div
                className="absolute rounded-full border border-gold/40"
                style={{
                  width: 200,
                  height: 200,
                  transform: `scale(${scale})`,
                  transition: "transform 60ms linear",
                }}
              />
              {/* centre text */}
              <div className="relative text-center text-pearl select-none">
                <p
                  className="font-display text-5xl sm:text-6xl tracking-wide"
                  data-testid="meditation-time-display"
                >
                  {formatTime(remaining)}
                </p>
                <p className="mt-2 text-[10px] uppercase tracking-[0.28em] text-gold">
                  {finished ? "Sit complete" : running ? phase : "Ready when you are"}
                </p>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="mt-10 flex items-center justify-center gap-3">
            <button
              onClick={handleStart}
              data-testid="meditation-start-pause"
              className="inline-flex items-center gap-2 px-7 py-3 bg-navy text-pearl rounded-full font-medium hover:bg-navy-light transition-colors"
            >
              {running ? (
                <>
                  <Pause size={18} /> Pause
                </>
              ) : finished ? (
                <>
                  <Play size={18} /> Sit again
                </>
              ) : remaining < minutes * 60 ? (
                <>
                  <Play size={18} /> Resume
                </>
              ) : (
                <>
                  <Play size={18} /> Begin
                </>
              )}
            </button>
            <button
              onClick={handleReset}
              data-testid="meditation-reset"
              className="inline-flex items-center gap-2 px-5 py-3 border border-navy/20 text-navy rounded-full hover:bg-navy/5 transition-colors"
              aria-label="Reset"
              title="Reset"
            >
              <RotateCcw size={18} />
            </button>
            <button
              onClick={() => setMuted((m) => !m)}
              data-testid="meditation-mute"
              className="inline-flex items-center gap-2 px-5 py-3 border border-navy/20 text-navy rounded-full hover:bg-navy/5 transition-colors"
              aria-label={muted ? "Unmute bell" : "Mute bell"}
              title={muted ? "Bell muted" : "Bell on"}
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>

          {finished && (
            <p
              className="mt-8 text-center font-display italic text-gold text-xl"
              data-testid="meditation-blessing"
            >
              Sabbe sattā sukhitā hontu, may all beings be happy.
            </p>
          )}
        </div>

        {/* Footnote */}
        <p className="mt-8 text-center text-xs text-slate-500 max-w-md mx-auto">
          Bell sounds are gently synthesised in your browser, no recording, no
          tracking, no servers. Tap the speaker to mute.
        </p>
      </div>
    </section>
  );
}
